import { useState, useEffect, useRef, useCallback } from 'react'
import { Building2, Search, Loader2, Check, AlertCircle } from 'lucide-react'
import { cn } from '@/utils/cn'
import { formFieldInputClass, formFieldMenuClass } from '@/components/shared/formFieldClasses'
import api from '@/services/api'
import type { EmpresaOcupacao } from '@/utils/leadRegistrationValidation'

export interface V2EmpresaOption {
  id: string
  nome: string
  site?: string
  segmento?: string
  plataforma?: string
  faturamento?: string
  cnpj?: string
  cidade?: string
  ocupacao?: EmpresaOcupacao | null
}

const OCUPACAO_COLORS: Record<string, { bg: string; border: string; text: string; label: string }> = {
  deal: { bg: 'rgba(239, 68, 68, 0.08)', border: 'rgba(239, 68, 68, 0.3)', text: '#f87171', label: 'Em Negociação' },
  closer: { bg: 'rgba(245, 158, 11, 0.08)', border: 'rgba(245, 158, 11, 0.3)', text: '#fbbf24', label: 'Com Closer' },
  bdr: { bg: 'rgba(16, 185, 129, 0.08)', border: 'rgba(16, 185, 129, 0.3)', text: '#34d399', label: 'Com BDR' },
}

interface CompanySearchSelectProps {
  value: string
  onSelect: (company: V2EmpresaOption) => void
  onChange?: (value: string) => void
  onClearSelection?: () => void
  error?: string
  highlighted?: boolean
  required?: boolean
}

export function CompanySearchSelect({
  value,
  onSelect,
  onChange,
  onClearSelection,
  error,
  highlighted = false,
  required = false,
}: CompanySearchSelectProps) {
  const [query, setQuery] = useState(value || '')
  const [results, setResults] = useState<V2EmpresaOption[]>([])
  const [loading, setLoading] = useState(false)
  const [showDropdown, setShowDropdown] = useState(false)
  const [selectedCompany, setSelectedCompany] = useState<V2EmpresaOption | null>(null)
  const [ocupacao, setOcupacao] = useState<EmpresaOcupacao | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (value !== undefined && value !== query) {
      setQuery(value)
    }
  }, [value])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const fetchCompanies = useCallback(async (search: string) => {
    if (search.trim().length < 1) {
      setResults([])
      setShowDropdown(false)
      return
    }
    setLoading(true)
    try {
      const res = await api.get(`/empresas/v2-search?q=${encodeURIComponent(search.trim())}`)
      const list: V2EmpresaOption[] = res.data?.data || []
      setResults(list)
      setShowDropdown(list.length > 0)
    } catch {
      setResults([])
    } finally {
      setLoading(false)
    }
  }, [])

  const handleInputChange = (v: string) => {
    setQuery(v)
    setSelectedCompany(null)
    setOcupacao(null)
    onChange?.(v)
    if (!v.trim()) {
      onClearSelection?.()
    }
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchCompanies(v), 400)
  }

  const handleSelect = (company: V2EmpresaOption) => {
    setSelectedCompany(company)
    setQuery(company.nome || '')
    setShowDropdown(false)
    setOcupacao(company.ocupacao || null)
    onSelect(company)
  }

  return (
    <div ref={wrapperRef} className='relative'>
      <label className='block font-medium text-text-muted uppercase tracking-wider mb-1.5' style={{ fontSize: 'var(--font-size-label)' }}>
        Buscar Empresa{required ? ' *' : ''}
      </label>
      <div className='relative'>
        <span className='absolute left-3 top-1/2 -translate-y-1/2 text-text-muted'>
          <Search size={14} />
        </span>
        <input
          type='text'
          value={query}
          onChange={e => handleInputChange(e.target.value)}
          onFocus={() => query.trim().length >= 1 && fetchCompanies(query)}
          placeholder='Digite para buscar empresa...'
          autoComplete='off'
          className={cn(
            formFieldInputClass,
            'rounded-lg pl-9 pr-9 py-2 text-[13px]',
            error && 'border-[#e5484d] focus:border-[#e5484d]',
            highlighted && !error && 'border-amber-500 ring-2 ring-amber-500/25 focus:border-amber-500',
          )}
        />
        {loading && (
          <span className='absolute right-3 top-1/2 -translate-y-1/2 text-text-muted'>
            <Loader2 size={14} className='animate-spin' />
          </span>
        )}
        {selectedCompany && !loading && (
          <span className='absolute right-3 top-1/2 -translate-y-1/2 text-accent'>
            <Check size={14} />
          </span>
        )}
      </div>
      {error && <span className='text-[11px] text-red-400 mt-1 block'>{error}</span>}

      {showDropdown && results.length > 0 && (
        <div
          className={cn(
            formFieldMenuClass,
            'absolute z-50 w-full mt-1 rounded-xl shadow-2xl max-h-52 overflow-y-auto border border-[var(--glass-border)] bg-[var(--bg-card)]',
          )}
        >
          {results.map(c => (
              <button
                key={c.id}
                type='button'
                onMouseDown={e => e.preventDefault()}
                onClick={() => handleSelect(c)}
                className='w-full text-left px-4 py-2.5 hover:bg-[var(--glass-bg)] flex items-center gap-3 transition-colors border-b border-[var(--glass-border)] last:border-b-0'
              >
                <div className='p-1.5 rounded-lg bg-accent/10 text-accent shrink-0'>
                  <Building2 size={14} />
                </div>
                <div className='min-w-0 flex-1'>
                  <span className='text-[13px] font-bold text-[var(--text-primary)] truncate block'>{c.nome || '---'}</span>
                  <span className='text-[10px] text-[var(--text-muted)] truncate block'>
                    {c.segmento || 'Sem segmento'}{c.cidade ? ` • ${c.cidade}` : ''}
                  </span>
                </div>
                {c.ocupacao && (
                  <span
                    className='text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded border shrink-0'
                    style={{
                      backgroundColor: OCUPACAO_COLORS[c.ocupacao.tipo]?.bg,
                      borderColor: OCUPACAO_COLORS[c.ocupacao.tipo]?.border,
                      color: OCUPACAO_COLORS[c.ocupacao.tipo]?.text,
                    }}
                  >
                    {OCUPACAO_COLORS[c.ocupacao.tipo]?.label}
                  </span>
                )}
              </button>
          ))}
        </div>
      )}

      {selectedCompany && (
        <div className='mt-2 p-3 rounded-lg bg-[var(--glass-bg)] border border-[var(--glass-border)]'>
          <div className='flex items-center gap-2'>
            <Building2 size={14} className='text-accent shrink-0' />
            <span className='text-[12px] font-bold text-[var(--text-primary)] truncate'>{selectedCompany.nome}</span>
          </div>
          {selectedCompany.cnpj && (
            <p className='text-[10px] text-text-muted mt-1 ml-6'>CNPJ: {selectedCompany.cnpj}</p>
          )}
        </div>
      )}

      {ocupacao && (
        <div
          className='mt-2 flex items-center gap-2 p-3 rounded-lg border'
          style={{
            backgroundColor: OCUPACAO_COLORS[ocupacao.tipo]?.bg || 'rgba(251, 191, 36, 0.08)',
            borderColor: OCUPACAO_COLORS[ocupacao.tipo]?.border || 'rgba(251, 191, 36, 0.3)',
            color: OCUPACAO_COLORS[ocupacao.tipo]?.text || '#fbbf24',
          }}
        >
          <AlertCircle size={16} className='shrink-0' />
          <div>
            <p className='text-[11px] font-bold uppercase'>
              {ocupacao.tipo === 'deal' || ocupacao.tipo === 'closer' ? 'Restrição de Entrada' : 'Aviso de Prospecção'}
            </p>
            <p className='text-[10px] opacity-90 mt-0.5'>{ocupacao.label}</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default CompanySearchSelect
